/* ----------------------------------------------------------------------------
 * Provider-neutral adapter registry (research doc §2 / §6.1 fallback).
 *
 * A single config value selects the ACTIVE STT/TTS provider; the AI Gateway /
 * Copilot brain never changes. 'mock' stays the deterministic, credential-free
 * default. 'deepgram' and 'amazon' now resolve to HTTP-proxy adapters that talk
 * ONLY to the server voice endpoints (/v1/ai/voice/*) — the browser never holds
 * a provider key: TTS streams from the server proxy, and STT records the
 * utterance then POSTs the audio to the server proxy (no token, no WebSocket).
 * Selecting a real provider stays gated by
 * GET /v1/ai/voice/status (voice_available) upstream; if the server has no key
 * the proxy returns 503 voice_unavailable and the adapters fail closed.
 * -------------------------------------------------------------------------- */

import type { SttAdapter } from '../stt/SttAdapter.ts';
import type { TtsAdapter } from '../tts/TtsAdapter.ts';
import { MockSttAdapter } from '../stt/MockSttAdapter.ts';
import { MockTtsAdapter } from '../tts/MockTtsAdapter.ts';
import { HttpSttAdapter } from '../net/HttpSttAdapter.ts';
import { HttpTtsAdapter } from '../net/HttpTtsAdapter.ts';

export type VoiceProvider = 'mock' | 'deepgram' | 'amazon';

export interface VoiceProviderConfig {
  /** active STT provider. */
  stt: VoiceProvider;
  /** active TTS provider. */
  tts: VoiceProvider;
  /** en-GB female voice id for the TTS provider (character contract §5.5). */
  ttsVoiceId: string;
}

/** Beta-safe default: everything mocked, no credentials required. */
export const DEFAULT_VOICE_PROVIDER_CONFIG: VoiceProviderConfig = {
  stt: 'mock',
  tts: 'mock',
  ttsVoiceId: 'aura-2-pandora-en',
};

/** Retained for API compatibility. The HTTP adapters now handle real providers
 *  via the server proxy; if the server itself is unprovisioned it returns 503
 *  voice_unavailable (surfaced as VoiceUnavailableError), so this is only thrown
 *  for a truly unknown provider key. */
export class ProviderNotProvisionedError extends Error {
  constructor(provider: VoiceProvider, kind: 'stt' | 'tts') {
    super(
      `Voice ${kind.toUpperCase()} provider "${provider}" is not recognised. ` +
        'Supported: "mock" (local) or a server-proxied provider ("deepgram" | "amazon").',
    );
    this.name = 'ProviderNotProvisionedError';
  }
}

export function createSttAdapter(provider: VoiceProvider): SttAdapter {
  switch (provider) {
    case 'mock':
      return new MockSttAdapter();
    case 'deepgram':
    case 'amazon':
      // Real provider: record + POST the utterance to the server proxy
      // (no key, no token, no WebSocket in the browser).
      return new HttpSttAdapter(provider);
    default: {
      const _never: never = provider;
      throw new ProviderNotProvisionedError(_never, 'stt');
    }
  }
}

export function createTtsAdapter(provider: VoiceProvider): TtsAdapter {
  switch (provider) {
    case 'mock':
      return new MockTtsAdapter();
    case 'deepgram':
    case 'amazon':
      // Real provider: synthesise via the server proxy (no key in browser).
      return new HttpTtsAdapter(provider);
    default: {
      const _never: never = provider;
      throw new ProviderNotProvisionedError(_never, 'tts');
    }
  }
}
